import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { useId } from 'react';
import type { PokemonV2 } from '../../pokemon/pokemon';
import type { Ability } from '../../types/ability';
import { getAvailableAbilities } from '../../lib/ability-access';

interface PokemonAbilitySelectProps {
  pokemon?: PokemonV2;
  abilityList?: Ability[];
  value?: number;
  onChange: (abilityId: number) => void;
  label?: string;
  variant?: 'sidebar' | 'card';
  // カード内でクリック伝播を止めたい場合に利用する
  stopPropagation?: boolean;
}

const resolveAbilityList = (pokemon?: PokemonV2, abilityList?: Ability[]): Ability[] => {
  if (abilityList && abilityList.length > 0) return abilityList;
  if (pokemon) return getAvailableAbilities(pokemon);
  return [];
};

function PokemonAbilitySelect({
  pokemon,
  abilityList,
  value,
  onChange,
  label = '特性を選択',
  variant = 'sidebar',
  stopPropagation,
}: PokemonAbilitySelectProps) {
  // ability_list が欠損していても候補が出せるようにフォールバックする
  const availableAbilities = resolveAbilityList(pokemon, abilityList);
  const shouldShowNoAbility = availableAbilities.length === 1;
  const isCardVariant = variant === 'card';
  const labelId = useId();

  return (
    <Box sx={isCardVariant ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } : undefined}>
      {isCardVariant ? (
        <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.25 }}>
          特性
        </Typography>
      ) : null}
      <FormControl size="small" sx={isCardVariant ? { width: '16ch' } : { width: '24ch' }}>
        {!isCardVariant && <InputLabel id={labelId}>{label}</InputLabel>}
        <Select
          labelId={isCardVariant ? undefined : labelId}
          label={isCardVariant ? undefined : label}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value as number)}
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
          sx={
            isCardVariant
              ? {
                  fontSize: '0.7rem',
                  '& .MuiSelect-select': {
                    padding: '2px 6px',
                    textAlign: 'center',
                  },
                }
              : undefined
          }
        >
          {availableAbilities.map((ability) => (
            <MenuItem
              key={ability.id}
              value={ability.id}
              sx={isCardVariant ? { fontSize: '0.7rem', justifyContent: 'center' } : undefined}
            >
              {ability.name_ja}
            </MenuItem>
          ))}
          {shouldShowNoAbility && (
            <MenuItem value={0} sx={isCardVariant ? { fontSize: '0.7rem', justifyContent: 'center' } : undefined}>
              とくせい無し
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
}

export default PokemonAbilitySelect;
